import React, { useEffect, useRef } from "react"
import * as am5 from "@amcharts/amcharts5"
import * as am5xy from "@amcharts/amcharts5/xy"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting"

// Utils
import { useCleanChart } from "../utils/hooks/useCleanChart"
import { getNumberFormat, transformeData } from "../utils/global"
import {
  createLegend,
  clearLegends,
  createExportMenu,
  configureNumberFormatting,
  resolveMappedColor,
} from "../utils/utils"
import type { ColorMapperRow } from "../utils/utils"
import { colors, typography } from "Shared/tokens"

// Types
interface CurveStyle {
  curveId?: string
  lineType?: "dashed" | "solid" | "dotted"
  bulletType?: "Rectangle" | "Triangle" | "Circle"
}

interface LineChartProps {
  idChart?: string
  widgetTitle?: string
  width?: string
  height?: string
  dataChart: any[]
  valueData: string[]
  categoryData: string
  dataNames?: string[]
  enableExportMenu?: boolean
  strokeWidthLine?: number
  formCurve?: number
  startLocation?: number
  endLocation?: number
  titleAxeX?: string
  titleAxeY?: string
  colorSetTheme?: string[]
  colorMapper?: ColorMapperRow[]
  textTooltipBefore?: string[]
  textTooltipAfter?: string[]
  fillOpacityCurve?: number
  disabledTooltipAxesX?: boolean
  disabledTooltipAxesY?: boolean
  disabledGridAxesX?: boolean
  disabledGridAxesY?: boolean
  lineStyle?: "dashed" | "solid" | "dotted"
  sizeBullet?: number
  numberDecimal?: number
  decimalSeparator?: string
  showSpaceForThousand?: boolean
  stackedLines?: boolean
  strokeWidthBullet?: number
  colorBullet?: string
  hoverBullet?: number
  visibilityLegend?: boolean
  displayTypeLegendLabels?: "truncate" | "wrap" | "default"
  legendPosition?: "top" | "bottom" | "left" | "right"
  visibilityZoom?: boolean
  positionScrollBar?: string
  axisStartZero?: boolean
  /** Graduations ENTIÈRES sur l'axe de valeurs (`maxPrecision: 0`). Même note que dans BarChart. */
  integerValueAxis?: boolean
  /**
   * Affiche la VALEUR de chaque point, juste au-dessus de lui.
   *
   * `LineChart` n'en posait aucune : lire une courbe demandait de survoler chaque point pour en
   * obtenir l'infobulle. Demandé par le client CRC le 2026-09-22 sur ses deux courbes mensuelles.
   *
   * ⚠️ Défaut `false` : sur une courbe dense, une étiquette par point est illisible. C'est un
   * réglage à poser quand les points sont peu nombreux — douze mois, par exemple.
   */
  showValueLabels?: boolean
  extraMinAxesY?: number
  extraMaxAxesY?: number
  widthGridValue?: number
  rotationLabelAxesX?: number
  fontSizeLabelAxesX?: number
  listOfAxisRangeObject?: any[]
  useLogarithm?: boolean
  displayTypeLabel?: "truncate" | "wrap" | "default"
  cumulValues?: boolean
  showAsPercentage?: boolean
  minYAxis?: number
  maxYAxis?: number
  useMinMaxYAxis?: boolean
  curvesStyles?: CurveStyle[]
  requestLoad?: any
  fieldsData?: any
  /**
   * Clic sur un POINT de la courbe — ouvre la liste des enregistrements sous-jacents.
   *
   * Même signature que sur les autres graphiques drillables : `categoryValue` est la valeur de
   * l'axe des catégories, `seriesIndex` la courbe cliquée. Sur un axe temporel groupé par période,
   * cette valeur est le libellé de la période (« Janv-26 ») et non une date : c'est à l'appelant
   * de la traduire en intervalle, ce que fait `containers/Widget`.
   */
  onSegmentClick?: (payload: { categoryValue: string; seriesIndex: number }) => void
}

const LineChart: React.FC<LineChartProps> = ({
  idChart,
  widgetTitle = "LineChart",
  width = "100%",
  height = "100%",
  dataChart = [],
  valueData = [],
  categoryData = "",
  dataNames = [],
  enableExportMenu = false,
  strokeWidthLine = 8,
  formCurve = 0.8,
  startLocation = 0.5,
  endLocation = 1,
  titleAxeX = "Year",
  titleAxeY = "Quantity",
  colorSetTheme = ["#04409C", "#FB9900", "#D20201"],
  colorMapper = [],
  textTooltipBefore = [],
  textTooltipAfter = [],
  fillOpacityCurve = 0.1,
  disabledTooltipAxesX = false,
  disabledTooltipAxesY = true,
  disabledGridAxesX = true,
  disabledGridAxesY = true,
  lineStyle = "solid",
  sizeBullet = 10,
  numberDecimal = 2,
  decimalSeparator = ",",
  showSpaceForThousand = true,
  stackedLines = false,
  strokeWidthBullet = 2,
  colorBullet = "#fff",
  hoverBullet = 1.5,
  visibilityLegend = true,
  displayTypeLegendLabels = "truncate",
  legendPosition = "bottom",
  visibilityZoom = false,
  positionScrollBar = "bottom",
  axisStartZero = false,
  integerValueAxis = false,
  showValueLabels = false,
  extraMinAxesY = 0.1,
  extraMaxAxesY = 0.1,
  widthGridValue = 30,
  rotationLabelAxesX = -45,
  fontSizeLabelAxesX = 12,
  listOfAxisRangeObject = [],
  useLogarithm = false,
  displayTypeLabel = "default",
  cumulValues = false,
  showAsPercentage = false,
  minYAxis,
  maxYAxis,
  useMinMaxYAxis = false,
  curvesStyles,
  requestLoad,
  fieldsData,
  onSegmentClick,
}) => {
  const ID = useRef(idChart || `curveChart${Math.random()}`).current
  const chartRef = useRef<am5xy.XYChart | null>(null)
  const rootRef = useRef<am5.Root | null>(null)

  const { cleanChart } = useCleanChart()
  const dataTransformed = transformeData(requestLoad, fieldsData, dataChart)

  const getStrokeDasharray = (lineStyle: string): number[] | undefined => {
    switch (lineStyle) {
      case "dashed":
        return [5, 5]
      case "dotted":
        return [2, 2]
      case "solid":
      default:
        return undefined
    }
  }

  const getCurveStyle = (
    valueDataId: string,
    curvesStyles?: CurveStyle[],
    defaultLineStyle?: string
  ): string => {
    if (!curvesStyles || !valueDataId) return defaultLineStyle || "solid"
    const curveStyleFound = curvesStyles.find(cs => cs?.curveId === valueDataId)

    return curveStyleFound?.lineType || defaultLineStyle || "solid"
  }

  const getBulletStyle = (valueDataId: string, curvesStyles?: CurveStyle[]): string => {
    if (!curvesStyles || !valueDataId) return ""
    const curveStyleFound = curvesStyles.find(cs => cs?.curveId === valueDataId)
    return curveStyleFound?.bulletType || ""
  }

  const makeDataCumulative = (dataChart: any[], valueData: string[]): any[] => {
    const newData: any[] = []
    dataChart.forEach((dataItem, index) => {
      if (index === 0) {
        newData.push({ ...dataItem })
        return
      }
      const item = { ...dataItem }
      valueData.forEach(key => {
        const previousValue = newData[index - 1]
        item[key] = item[key] + previousValue[key]
      })
      newData.push({ ...item })
    })
    return newData
  }

  const getTooltipBefore = (index: number): string => {
    if (textTooltipBefore.length > index) return textTooltipBefore[index]
    if (dataNames.length > index) return dataNames[index]
    return "undefined"
  }

  const getTooltipAfter = (index: number): string => {
    return textTooltipAfter.length > index ? textTooltipAfter[index] : ""
  }

  const initChart = () => {
    const root = am5.Root.new(ID)
    root._logo?.dispose()
    rootRef.current = root

    root.setThemes([am5themes_Animated.new(root)])

    // Language configuration
    if (decimalSeparator === ",") {
      root.numberFormatter.set("numberFormat", "#,###.##")
    }

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        layout: root.verticalLayout,
      })
    )

    chartRef.current = chart

    const processedData = cumulValues ? makeDataCumulative(dataChart, valueData) : dataChart

    setupChart(chart, root)

    // Cursor
    chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "none",
      })
    )

    // Build axes, series, and legend
    rebuildChartStructure(chart, root, processedData)
  }

  const rebuildChartStructure = (chart: am5xy.XYChart, root: am5.Root, processedData: any[]) => {
    // Category axis (X)
    const categoryAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: categoryData,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: widthGridValue,
          cellStartLocation: startLocation,
          cellEndLocation: endLocation,
        }),
        tooltip: disabledTooltipAxesX ? undefined : am5.Tooltip.new(root, {}),
      })
    )

    categoryAxis.get("renderer").labels.template.setAll({
      rotation: rotationLabelAxesX,
      fontSize: fontSizeLabelAxesX,
      oversizedBehavior:
        displayTypeLabel === "truncate"
          ? "truncate"
          : displayTypeLabel === "wrap"
          ? "wrap"
          : "none",
      maxWidth: 150,
      tooltipText: "{category}",
    })

    categoryAxis.get("renderer").grid.template.set("visible", !disabledGridAxesX)

    // X-axis title
    if (titleAxeX) {
      categoryAxis.children.push(
        am5.Label.new(root, {
          text: titleAxeX,
          x: am5.p50,
          centerX: am5.p50,
        })
      )
    }

    categoryAxis.data.setAll(processedData)

    // Value axis (Y)
    const valueAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        logarithmic: useLogarithm,
        tooltip: disabledTooltipAxesY ? undefined : am5.Tooltip.new(root, {}),
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: widthGridValue,
        }),
      })
    )

    const axisTitle = showAsPercentage ? `${titleAxeY} (%)` : titleAxeY
    valueAxis.children.unshift(
      am5.Label.new(root, {
        text: axisTitle,
        rotation: -90,
        y: am5.p50,
        centerX: am5.p50,
      })
    )

    valueAxis.get("renderer").grid.template.set("visible", !disabledGridAxesY)

    // Graduations entieres : sans ce reglage, un pas fractionnaire choisi par amCharts sur de
    // petites valeurs produit deux libelles identiques apres arrondi d'affichage.
    if (integerValueAxis) {
      valueAxis.set("maxPrecision", 0)
    }

    if (useMinMaxYAxis && minYAxis !== undefined && maxYAxis !== undefined && minYAxis < maxYAxis) {
      valueAxis.set("min", minYAxis)
      valueAxis.set("max", maxYAxis)
      valueAxis.set("strictMinMax", true)
    } else if (axisStartZero) {
      valueAxis.set("min", 0)
      valueAxis.set("extraMin", 0)
      valueAxis.set("extraMax", extraMaxAxesY)
    } else {
      valueAxis.set("extraMin", extraMinAxesY)
      valueAxis.set("extraMax", extraMaxAxesY)
    }

    // Axis ranges
    listOfAxisRangeObject.forEach(rangeObj => {
      const axisRange = valueAxis.createAxisRange(
        valueAxis.makeDataItem({
          value: rangeObj.value,
        })
      )

      axisRange.get("grid")?.setAll({
        stroke: am5.color(rangeObj.strokeColor),
        strokeWidth: rangeObj.strokeWidth,
        strokeOpacity: rangeObj.strokeOpacity,
      })

      axisRange.get("label")?.setAll({
        text: rangeObj.textLabel,
        fill: am5.color(rangeObj.colorLabel),
        inside: rangeObj.inside,
        centerY:
          rangeObj.position === "top" ? 0 : rangeObj.position === "bottom" ? am5.p100 : am5.p50,
      })
    })

    // Add series
    addSerieToChart(chart, root, processedData)

    // Legend
    const legend = createLegend(chart, root, {
      visibilityLegend,
      legendPosition,
      displayTypeLegendLabels,
    })

    if (legend) {
      const seriesArray: any[] = []
      chart.series.each((series: am5.Series) => {
        seriesArray.push(series)
      })
      legend.data.setAll(seriesArray)
    }
  }

  const setupChart = (chart: am5xy.XYChart, root: am5.Root) => {
    // Number formatting
    configureNumberFormatting(root, {
      decimalSeparator,
      numberDecimal,
      showSpaceForThousand,
    })

    // Export menu
    createExportMenu(root, {
      enableExportMenu: enableExportMenu ?? false,
      filePrefix: widgetTitle,
      dataSource: dataTransformed,
    })

    // Scrollbar
    if (visibilityZoom) {
      const scrollbarX = am5xy.XYChartScrollbar.new(root, {
        orientation: "horizontal",
      })
      chart.set("scrollbarX", scrollbarX)
    }
  }

  const addSerieToChart = (chart: am5xy.XYChart, root: am5.Root, data: any[]) => {
    for (let i = 0; i < valueData.length; i++) {
      const nameToUse = i < dataNames.length ? dataNames[i] : "Undefined Name"
      const paletteColor = i < colorSetTheme.length ? colorSetTheme[i] : colorSetTheme[0]
      // Une courbe EST une valeur de legende : une regle valeur -> couleur l'emporte sur la palette.
      const colorIndex = resolveMappedColor(nameToUse, colorMapper) ?? paletteColor
      const curveStyle = getCurveStyle(valueData[i], curvesStyles, lineStyle)
      const bulletStyleType = getBulletStyle(valueData[i], curvesStyles)

      const series = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: nameToUse,
          xAxis: chart.xAxes.getIndex(0) as am5xy.CategoryAxis<am5xy.AxisRenderer>,
          yAxis: chart.yAxes.getIndex(0) as am5xy.ValueAxis<am5xy.AxisRenderer>,
          valueYField: valueData[i],
          categoryXField: categoryData,
          stacked: stackedLines,
          stroke: am5.color(colorIndex),
          fill: am5.color(colorIndex),
          tooltip: am5.Tooltip.new(root, {
            labelText: `${getTooltipBefore(i)}: [bold]{valueY}[/] ${getTooltipAfter(i)}${
              showAsPercentage ? "%" : ""
            }`,
          }),
        })
      )

      series.strokes.template.setAll({
        strokeWidth: strokeWidthLine,
        strokeDasharray: getStrokeDasharray(curveStyle),
      })

      series.fills.template.setAll({
        fillOpacity: fillOpacityCurve,
        visible: true,
      })

      /**
       * Rend une puce cliquable pour le drill-down.
       *
       * ⚠️ On lie l'événement sur la FORME visible, jamais sur le conteneur : un conteneur
       * amCharts ne reçoit pas les événements de pointeur tant qu'aucun de ses enfants n'est
       * interactif, et le clic tomberait dans le vide sans que rien ne le signale.
       *
       * Le curseur passe en « pointer » : ce qui est cliquable doit se voir comme tel.
       */
      const rendreCliquable = (forme: am5.Graphics) => {
        if (!onSegmentClick) return
        forme.set("cursorOverStyle", "pointer")
        forme.events.on("click", ev => {
          const dataItem = ev.target.dataItem as any
          if (!dataItem) return
          const categoryValue = dataItem.get("categoryX")
          if (categoryValue !== undefined) {
            onSegmentClick({ categoryValue: String(categoryValue), seriesIndex: i })
          }
        })
      }

      // Bullets
      if (bulletStyleType) {
        series.bullets.push(() => {
          const bulletContainer = am5.Container.new(root, {})
          let bulletShape: am5.Graphics

          switch (bulletStyleType) {
            case "Rectangle":
              bulletShape = am5.Rectangle.new(root, {
                width: sizeBullet * 2.5,
                height: sizeBullet * 2.5,
                fill: am5.color(colorBullet),
                centerX: am5.p50,
                centerY: am5.p50,
                stroke: am5.color(colorIndex),
                strokeWidth: strokeWidthBullet,
              })
              break
            case "Triangle":
              bulletShape = am5.Triangle.new(root, {
                width: sizeBullet * 2.5,
                height: sizeBullet * 2.5,
                fill: am5.color(colorBullet),
                centerX: am5.p50,
                centerY: am5.p50,
                stroke: am5.color(colorIndex),
                strokeWidth: strokeWidthBullet,
              })
              break
            case "Circle":
            default:
              bulletShape = am5.Circle.new(root, {
                radius: sizeBullet,
                fill: am5.color(colorBullet),
                stroke: am5.color(colorIndex),
                strokeWidth: strokeWidthBullet,
              })
              break
          }

          bulletContainer.children.push(bulletShape)

          // Add hover state
          bulletShape.states.create("hover", {
            scale: hoverBullet,
          })

          rendreCliquable(bulletShape)

          return am5.Bullet.new(root, {
            sprite: bulletContainer,
          })
        })
      } else {
        series.bullets.push(() => {
          const circle = am5.Circle.new(root, {
            radius: sizeBullet,
            fill: am5.color(colorBullet),
            strokeWidth: strokeWidthBullet,
            stroke: am5.color(colorIndex),
          })

          // Add hover state
          circle.states.create("hover", {
            scale: hoverBullet,
          })

          rendreCliquable(circle)

          return am5.Bullet.new(root, {
            sprite: circle,
          })
        })
      }

      /**
       * ÉTIQUETTE DE VALEUR au-dessus de chaque point.
       *
       * Une puce SÉPARÉE, et non un enfant du conteneur de puce : celui-ci porte des états de survol
       * et un agrandissement au passage de la souris (`hoverBullet`), qui feraient grossir le texte
       * avec la forme. Deux puces se superposent sans se gêner.
       *
       * ⚠️ `locationY` n'existe pas sur une puce de courbe — le point est déjà placé par sa valeur.
       * Le décalage se fait donc au `centerY` de l'étiquette, comme pour le total d'une pile de
       * barres.
       */
      if (showValueLabels) {
        series.bullets.push(() => {
          const etiquette = am5.Label.new(root, {
            text: showAsPercentage ? "{valueY}%" : "{valueY}",
            fill: am5.color(colors.textPrimary),
            centerX: am5.p50,
            centerY: am5.p100,
            dy: -(sizeBullet + 4),
            fontSize: typography.caption.size,
            // Même raison que les étiquettes hors barre : posée sur le fond de la carte, au milieu
            // de la grille et des libellés d'axe, une valeur maigre se lit comme du décor.
            // ⚠️ `"bold"` et non `"600"` : Roboto n'a pas de graisse 600 (cf. `BarChart`).
            fontWeight: "bold",
            populateText: true,
          })
          return am5.Bullet.new(root, { sprite: etiquette })
        })
      }

      series.data.setAll(data)
    }
  }

  // Effect for initial chart creation only
  useEffect(() => {
    initChart()

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose()
        rootRef.current = null
        chartRef.current = null
      }
    }
  }, [])

  // Effect for updates
  useEffect(() => {
    if (chartRef.current && rootRef.current) {
      const chart = chartRef.current
      const root = rootRef.current

      const processedData = cumulValues ? makeDataCumulative(dataChart, valueData) : dataChart

      // Clean chart and clear legends
      cleanChart(chart)
      clearLegends(chart)

      // Reapply chart configurations
      setupChart(chart, root)

      // Clear and rebuild axes and series
      chart.xAxes.clear()
      chart.yAxes.clear()
      chart.series.clear()

      // Rebuild axes, series, and legend
      rebuildChartStructure(chart, root, processedData)
    }
  })

  return <div id={ID} style={{ width, height }} />
}

export default LineChart
